import {
	FormControl,
	FormControlProps,
	FormHelperText,
	InputAdornment,
	TextField,
	TextFieldProps,
} from '@mui/material';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import useErrorScrollContext from '../../hooks/useErrorScrollContext';
import { divide, multiply } from '../../lib/helpers/math';
import { isNumeric } from '../../lib/helpers/types';

// Frozen variables for controlling key presses
const ControlKeys = [ 'Control', 'Meta' ] as const;
const ControlKeyModifiers = [
	'c',
	'v',
	'x'
] as const;
const ManipulationKeys = [
	'Tab',
	'Enter',
	'Backspace',
	'Delete',
	'ArrowLeft',
	'ArrowRight',
	'Home',
	'End',
	'Shift',
] as const;

// make it easier to change the current to eventually support canada or australia
const decimalCharacter = '.';
const thousandsCharacter = ',';

const currencyCharacter = '$';

const USDollar = new Intl.NumberFormat( 'en-US', {
	style: 'currency',
	currency: 'USD',
} );

type MuiCurrencyInputPropsOnChange = ( value: number ) => void;
type MuiCurrencyInputProps = {
	errorText?: string;
	value?: number | null;
	onChange?: MuiCurrencyInputPropsOnChange; // custom onChange to handle type=number value
	min?: number;
	max?: number;
	limit?: RegExp;
	blankWhenZero?: true;
	onBlur?: ( forceValidation?: boolean ) => void;
} & Omit<
	TextFieldProps,
	'value' | 'onChange' | 'error' | 'variant' | 'onBlur'
> &
	Pick<FormControlProps, 'variant'>;

const MuiCurrencyInput: React.FC<MuiCurrencyInputProps> = ( props ) => {
	const {
		className,
		fullWidth,
		errorText,
		variant,
		min,
		max,
		limit,
		blankWhenZero,
		...propsToSpread
	} = props;
	const ref = useErrorScrollContext( !!errorText );
	const [ displayAmount, setDisplayAmount ] = useState( '' );
	// so we detect when the control key is down to allow ctrl+x but disallow just x, without re-rendering
	const controlKeyDownRef = useRef( false );
	const displayValueHasDecimal = displayAmount.indexOf( decimalCharacter ) > 0;
	const convertDisplayAmountAndFormat = useCallback( () => {
		// sanitize value to remove commas
		const stringValue = displayAmount.replace(
			new RegExp( thousandsCharacter, 'g' ),
			''
		);
		// only allow one period, and assume dollars not pennies is the intent
		const [ dollars, ...rest ] =
			stringValue.indexOf( decimalCharacter ) > -1
				? stringValue.split( decimalCharacter )
				: [ stringValue, '00' ];
		// this way if the user enters '12.34.56' that gets converted into dollars=12 and cents=3456
		const cents = rest.join();
		// convert to a dollar amount
		const formattedAmount = USDollar.format(
			parseFloat( `${ dollars }${ decimalCharacter }${ cents }` )
		).replace( currencyCharacter, '' );
		const numberValue = multiply(
			parseFloat(
				formattedAmount.replace( new RegExp( `[${ thousandsCharacter }]`, 'g' ), '' )
			),
			100
		);
		return { numberValue, formattedAmount };
	}, [ displayAmount ] );
	// Format the value
	const handleOnBlur = useCallback( () => {
		const { numberValue, formattedAmount } = convertDisplayAmountAndFormat();
		setDisplayAmount( formattedAmount );
		props.onChange?.( numberValue );
		props.onBlur?.();
	}, [ convertDisplayAmountAndFormat, props ] );
	const handleKeyDown = useCallback(
		( e: React.KeyboardEvent<HTMLDivElement> ) => {
			const isCurrencyKey = new RegExp(
				`[\\d${ thousandsCharacter }${ decimalCharacter }]`
			).test( e.key );
			const isAllowedNonCurrencyKey = ( [] as string[] )
				.concat( ManipulationKeys )
				.concat( ...ControlKeys )
				.concat( controlKeyDownRef.current ? ControlKeyModifiers : [] )
				.includes( e.key );
			// Also, don't allow more than 1 period in the value
			if ( ( ControlKeys as unknown as string[] ).includes( e.key ) ) {
				controlKeyDownRef.current = true;
			}
			const isNthPeriod = e.key === decimalCharacter && displayValueHasDecimal;
			if ( ( !isCurrencyKey && !isAllowedNonCurrencyKey ) || isNthPeriod ) {
				e.preventDefault();
			}
			if ( e.key === 'Enter' || e.key === 'Tab' ) {
				/* Clicking on buttons causes blur to fire here before submit on the
				parent, but not the enter key. So in order to get the right currency
				value ensure onBlur fires before onSubmit */
				handleOnBlur();
			}
		},
		[ displayValueHasDecimal, handleOnBlur ]
	);
	const handleKeyUp = useCallback( ( e: React.KeyboardEvent<HTMLDivElement> ) => {
		const isLiterallyControlKey = ( [] as string[] )
			.concat( ControlKeys )
			.includes( e.key );
		if ( isLiterallyControlKey ) {
			controlKeyDownRef.current = false;
		}
	}, [] );
	const isBelowMax = useCallback(
		( value: string ) =>
			( typeof max === 'number' && parseFloat( value ) <= max ) ||
			typeof max == 'undefined',
		[ max ]
	);
	const isAboveMin = useCallback(
		( value: string ) =>
			( typeof min === 'number' && parseFloat( value ) >= min ) ||
			typeof min == 'undefined',
		[ min ]
	);
	const isWithinLimit = useCallback(
		( value: string ) =>
			( limit && limit.test( value ) ) || typeof limit === 'undefined',
		[ limit ]
	);
	const isValidValue = useCallback(
		( value: string ) =>
			value === ''
				? true
				: isBelowMax( value ) && isAboveMin( value ) && isWithinLimit( value ),
		[
			isAboveMin,
			isBelowMax,
			isWithinLimit
		]
	);
	const handleOnChange = useCallback(
		( e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement> ) => {
			const sanitizedValue = e.target.value?.replace(
				new RegExp( `[${ decimalCharacter }]+`, 'g' ),
				decimalCharacter
			);
			if ( isValidValue( sanitizedValue ) ) {
				setDisplayAmount( sanitizedValue );
			}
		},
		[ isValidValue ]
	);
	// Make sure when the prop changes this value changes - but if it's not a number, do nothing
	useEffect( () => {
		if ( isNumeric( props.value ) ) {
			// assuming input of pennies
			const numberValue = parseFloat( props.value as unknown as string );
			const formattedAmount = USDollar.format( divide( numberValue, 100 ) ).replace(
				'$',
				''
			);
			setDisplayAmount( formattedAmount );
		}
	}, [ props.value, setDisplayAmount ] );
	return (
		<FormControl
			variant={ variant }
			error={ !!props.errorText }
			fullWidth={ fullWidth }
			className={ className }
		>
			<TextField
				// Hint do the browser the type of this field - before the spread so we can override it
				autoComplete='transaction-amount'
				{ ...propsToSpread }
				fullWidth={ fullWidth }
				variant={ variant }
				value={
					blankWhenZero && displayAmount === `0${ decimalCharacter }00`
						? ''
						: displayAmount
				}
				// allow numbers, periods, commas, and some control-keys
				onKeyDown={ handleKeyDown }
				onKeyUp={ handleKeyUp }
				onChange={ handleOnChange }
				onBlur={ handleOnBlur }
				slotProps={ {
					...propsToSpread.slotProps,
					input: {
						...propsToSpread.slotProps?.input,
						startAdornment: (
							<InputAdornment disableTypography position='start'>
								$
							</InputAdornment>
						),
					},
					htmlInput: {
						...propsToSpread.slotProps?.htmlInput,
						/* this is better than type=number in that it counts "12."" as a valid value.
						Also, decimal is better than numeric because on iOS this includes numbers AND a period in the keyboard */
						inputMode: 'decimal',
					},
				} }
				error={ !!props.errorText }
				ref={ ref }
			/>
			{ props.errorText ? (
				<FormHelperText>{ props.errorText }</FormHelperText>
			) : null }
		</FormControl>
	);
};

export default MuiCurrencyInput;
